/**
 * The learner's own profile.
 *
 * Structure follows the prototype's `screen-profile`: a gradient banner with the avatar
 * overlapping it, a name block with a meta row, then a two-column grid with the main
 * sections on the left and a narrow aside. Class names are the prototype's
 * (`profile__head`, `profile__banner`, `profile__grid`, `profile__stats`,
 * `profile__activity`) so the two can be put side by side and compared.
 *
 * The prototype filled that grid with upcoming deadlines, a skills-and-interests chip
 * cloud and an activity feed. None of those exist as account data on a stock Open edX —
 * there is no skills field, no per-learner deadline API on the account, and no activity
 * stream — so the same slots hold what the platform genuinely knows: the about-me, the
 * certificates it has issued, and enrolment counts from the learner-home endpoint the
 * dashboard already uses.
 *
 * WHAT OTHER PEOPLE SEE. `account_privacy` is not decoration here. The server returns a
 * different, much smaller payload to anyone reading a private account, so the page
 * explains which state it is in rather than implying everything on screen is public.
 * See the header of `shared/lib/account.ts`.
 *
 * Colour is entirely tokens, so the banner is whatever the academy configured.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Icon, Spinner } from '../../shared/ui';
import { loadSession } from '../../shared/lib/session';
import { courseIdVisibleToTenant, getMyCourses } from '../../shared/lib/catalogue';
import {
  certificateViewUrl,
  formatIssued,
  listEarnedCertificates,
} from '../../shared/lib/certificates';
import {
  type Account,
  avatarUrl,
  countryName,
  displayName,
  explainSaveFailure,
  fieldErrors,
  getAccount,
  getFieldOptions,
  initialsOf,
  joinedLabel,
  languageNames,
  patchAccount,
  PUBLIC_PROFILE_FIELDS,
  SOCIAL_PLATFORMS,
  socialLinkFor,
  VISIBILITY_ALL,
  visibilityOf,
} from '../../shared/lib/account';
import './account.css';

export default function Profile() {
  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const username = session.data?.user?.username;

  const account = useQuery({
    queryKey: ['account', username],
    queryFn: () => getAccount(username as string),
    enabled: Boolean(username),
  });

  // Counts only, so a slow dashboard read must never hold up the profile itself.
  const home = useQuery({ queryKey: ['my-courses'], queryFn: getMyCourses, enabled: Boolean(username) });

  // THE CERTIFICATES COME FROM THE CERTIFICATES ENDPOINT, not from the account record.
  //
  // `account.course_certificates` looks like the obvious source and is a trap: it is one of
  // the SHAREABLE profile fields, so the platform returns it only when the learner has made
  // their profile public. Measured for a learner holding two certificates with the default
  // (unset, therefore private) visibility: `visible_fields` was
  // ['account_privacy', 'profile_image', 'username'] and `course_certificates` came back null
  // — so this page told its own owner "No certificates yet" while their certificates page
  // listed two. Nobody is being protected by that: this is the learner looking at themselves.
  const earned = useQuery({
    queryKey: ['earned-certificates', username],
    queryFn: () => listEarnedCertificates(username as string),
    enabled: Boolean(username),
  });

  const [editing, setEditing] = useState(false);

  if (session.isLoading || (username && account.isLoading)) {
    return (
      <main className="profile">
        <Spinner label="Loading your profile…" />
      </main>
    );
  }

  if (!username) {
    return (
      <main className="profile">
        <Banner tone="warning">
          <strong>You are signed out.</strong> Sign in to see and edit your profile.
        </Banner>
      </main>
    );
  }

  if (account.isError) {
    return (
      <main className="profile">
        <Banner tone="danger">
          <strong>We could not load your profile.</strong> Nothing has been changed — this was
          only the request to read it. Reload the page to try again.
        </Banner>
      </main>
    );
  }

  const me = account.data;
  const privacy = visibilityOf(me);
  const name = displayName(me);
  const photo = avatarUrl(me);
  const country = countryName(me?.country);
  const joined = joinedLabel(me?.date_joined);
  const languages = languageNames(me);

  const courses = home.data ?? [];
  const completed = courses.filter(
    (entry) => entry.certificate.isEarned || entry.gradeData.isPassing,
  ).length;
  const inProgress = courses.filter(
    (entry) => entry.enrollment.hasStarted && !entry.certificate.isEarned && !entry.gradeData.isPassing,
  ).length;
  const certificates = courses.filter((entry) => entry.certificate.isEarned).length;

  // Narrowed to this academy, by org OR licence, exactly as the certificates page does — the
  // endpoint returns every certificate the learner holds anywhere, and one academy's profile
  // listing another's credentials would be a leak between tenants.
  const myCertificates = (earned.data ?? []).filter((cert) =>
    courseIdVisibleToTenant(cert.course_id),
  );

  const links = SOCIAL_PLATFORMS.map((platform) => ({
    ...platform,
    url: socialLinkFor(me, platform.id),
  })).filter((platform) => platform.url);

  return (
    <main className="profile">
      <div className="profile__head">
        <div className="profile__banner">
          <div className="profile__banner-actions">
            {editing ? null : (
              <button type="button" className="ui-btn" data-variant="secondary" onClick={() => setEditing(true)}>
                <Icon name="edit" />
                Edit profile
              </button>
            )}
          </div>
        </div>

        <div className="profile__head-body">
          <div className="profile__avatar">
            {photo ? <img src={photo} alt="" /> : initialsOf(me) || <Icon name="person" />}
          </div>
          <div className="profile__info">
            <h1>{name}</h1>
            <div className="role">@{me?.username}</div>
            <div className="meta-row">
              {country && (
                <span>
                  <Icon name="location_on" />
                  {country}
                </span>
              )}
              {languages && (
                <span>
                  <Icon name="translate" />
                  {languages}
                </span>
              )}
              {joined && (
                <span>
                  <Icon name="calendar_today" />
                  Joined {joined}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {editing && username && (
        <ProfileEditor
          username={username}
          account={me}
          onDone={() => setEditing(false)}
        />
      )}

      <div className="profile__grid">
        <div>
          <section className="profile__section">
            <h3>About</h3>
            <div className="profile__card">
              {me?.bio ? (
                <p className="profile__bio">{me.bio}</p>
              ) : (
                <p className="profile__muted">
                  You have not written an about-me yet. It is the first thing other learners see
                  next to your name in discussions.{' '}
                  <button type="button" className="profile__inline-link" onClick={() => setEditing(true)}>
                    Add one
                  </button>
                  .
                </p>
              )}
            </div>
          </section>

          <section className="profile__section">
            <h3>Certificates</h3>
            {myCertificates.length > 0 ? (
              <div className="profile__certs">
                {myCertificates.map((cert) => {
                  const href = certificateViewUrl(cert);
                  return (
                    <div className="profile__cert" key={cert.uuid || cert.course_id}>
                      <span className="profile__seal" aria-hidden="true">
                        <Icon name="workspace_premium" />
                      </span>
                      <div className="profile__cert-body">
                        <h4>{cert.course_display_name || cert.course_id}</h4>
                        <p>
                          {cert.certificate_type ? `${cert.certificate_type} · ` : ''}
                          Issued {formatIssued(cert.created_date)}
                        </p>
                      </div>
                      {/* A plain `<a>`: the certificate page is reached through the proxy and
                          the router must not answer it. */}
                      {href && (
                        <a className="ui-btn" data-variant="secondary" href={href}>
                          View
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="profile__card">
                <p className="profile__muted">
                  No certificates yet. They appear here as soon as a course you have passed issues
                  one.
                </p>
              </div>
            )}
          </section>

          {links.length > 0 && (
            <section className="profile__section">
              <h3>Links</h3>
              <div className="profile__card profile__links">
                {links.map((platform) => (
                  <a key={platform.id} href={platform.url} rel="noreferrer noopener" target="_blank">
                    <Icon name="link" />
                    {platform.label}
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside>
          <section className="profile__section">
            <h3>Learning at a glance</h3>
            {home.isError ? (
              <div className="profile__card">
                <p className="profile__muted">
                  Your course counts could not be loaded. Your enrolments are unaffected — reload
                  to try again.
                </p>
              </div>
            ) : home.isLoading ? (
              <div className="profile__card">
                <p className="profile__muted">Counting your courses…</p>
              </div>
            ) : (
              <div className="profile__card profile__stats">
                <div className="profile__stat">
                  <strong>{courses.length}</strong>
                  <span>Enrolled</span>
                </div>
                <div className="profile__stat">
                  <strong>{inProgress}</strong>
                  <span>In progress</span>
                </div>
                <div className="profile__stat">
                  <strong>{completed}</strong>
                  <span>Completed</span>
                </div>
                <div className="profile__stat">
                  <strong>{certificates}</strong>
                  <span>Certificates</span>
                </div>
              </div>
            )}
          </section>

          <section className="profile__section">
            <h3>Who can see this</h3>
            <div className="profile__card">
              {privacy.forced ? (
                <>
                  <p className="profile__muted">
                    Your profile is private to everyone else, and the academy is keeping it that
                    way because it has no year of birth on file for you.
                  </p>
                  <p className="profile__muted">
                    Add one in settings and you will be able to choose whether other learners can
                    see your profile.
                  </p>
                </>
              ) : privacy.isPublic ? (
                <p className="profile__muted">
                  Other learners can see {PUBLIC_PROFILE_FIELDS}. Your real name, email address
                  and course results stay private either way.
                </p>
              ) : (
                <p className="profile__muted">
                  Your profile is private. Other learners see only your photo and username — not
                  your about-me, country or links.
                </p>
              )}
              <Link className="ui-btn" data-variant="ghost" to="/settings">
                Change in settings
              </Link>
            </div>
          </section>
        </aside>
      </div>
    </main>
  );
}

/* ---------------------------------------------------------------- editing ------- */

/**
 * Inline editor for the fields that make up the profile above.
 *
 * Deliberately a subset: name, about-me, country and visibility are what this page
 * shows, so they are what it edits. Everything else — email, photo, notifications,
 * time zone — lives on the settings page, and both save through the same
 * `patchAccount`, so there is one write path rather than two that can disagree.
 *
 * `country` only appears when the platform gave us its own list of country codes. See
 * `getFieldOptions`: on an install where country is not a registration field there is no
 * server-sanctioned vocabulary, and offering codes we invented risks writing a value the
 * server will later reject.
 */
function ProfileEditor({
  username,
  account,
  onDone,
}: {
  username: string;
  account: Account | undefined;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const privacy = visibilityOf(account);

  const [name, setName] = useState(account?.name ?? '');
  const [bio, setBio] = useState(account?.bio ?? '');
  const [country, setCountry] = useState(account?.country ?? '');
  const [isPublic, setIsPublic] = useState(privacy.isPublic);

  // The form opens from cached data; if a fresher account lands underneath it, adopt it
  // rather than saving stale values over the top.
  useEffect(() => {
    setName(account?.name ?? '');
    setBio(account?.bio ?? '');
    setCountry(account?.country ?? '');
    setIsPublic(visibilityOf(account).isPublic);
  }, [account]);

  const options = useQuery({
    queryKey: ['account-field-options'],
    queryFn: getFieldOptions,
    staleTime: 60 * 60_000,
  });
  const countries = options.data?.country;

  const save = useMutation({
    mutationFn: (patch: Partial<Account>) => patchAccount(username, patch),
    onSuccess: (updated) => {
      queryClient.setQueryData(['account', username], updated);
      onDone();
    },
  });

  const errors = fieldErrors(save.error);

  return (
    <form
      className="profile__editor"
      onSubmit={(event) => {
        event.preventDefault();
        const patch: Partial<Account> = {
          name: name.trim(),
          bio: bio.trim(),
          // Only send privacy when the server will actually honour it — while parental
          // consent is outstanding it is overridden, and writing it would look like a
          // save that did nothing.
          ...(privacy.forced ? {} : { account_privacy: isPublic ? VISIBILITY_ALL : 'private' }),
          ...(countries ? { country: country || null } : {}),
        };
        save.mutate(patch);
      }}
    >
      <h2>Edit profile</h2>

      {save.isError && (
        <Banner tone="danger">
          <strong>Not saved.</strong> {explainSaveFailure(save.error, 'profile')}
        </Banner>
      )}

      <div className="settings-field">
        <label htmlFor="profile-name">Full name</label>
        <input
          id="profile-name"
          value={name}
          maxLength={255}
          onChange={(event) => setName(event.target.value)}
          autoComplete="name"
        />
        <p className="settings-field__hint">
          {errors.name ?? 'Only staff and the academy team see this. Other learners see your username.'}
        </p>
      </div>

      <div className="settings-field">
        <label htmlFor="profile-bio">About me</label>
        <textarea
          id="profile-bio"
          rows={4}
          value={bio}
          maxLength={300}
          onChange={(event) => setBio(event.target.value)}
        />
        <p className="settings-field__hint">
          {errors.bio ?? `${bio.length} of 300 characters.`}
        </p>
      </div>

      {countries && (
        <div className="settings-field">
          <label htmlFor="profile-country">Country</label>
          <select
            id="profile-country"
            value={country}
            onChange={(event) => setCountry(event.target.value)}
          >
            <option value="">Prefer not to say</option>
            {countries.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          {errors.country && <p className="settings-field__hint">{errors.country}</p>}
        </div>
      )}

      {!privacy.forced && (
        <div className="toggle-row">
          <div className="toggle-row__body">
            <strong>Show my profile to other learners</strong>
            <span>They see {PUBLIC_PROFILE_FIELDS}.</span>
          </div>
          <button
            type="button"
            className={`toggle${isPublic ? ' is-on' : ''}`}
            role="switch"
            aria-checked={isPublic}
            aria-label="Show my profile to other learners"
            onClick={() => setIsPublic((on) => !on)}
          />
        </div>
      )}

      <div className="settings-card__foot">
        <Button variant="ghost" type="button" onClick={onDone} disabled={save.isPending}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" busy={save.isPending}>
          Save changes
        </Button>
      </div>
    </form>
  );
}
